import { isGuiSession } from '../../workspace/config/guiSessionConfig'
import { isAIEnabled } from '../../workspace/service/languageConfig'
import { isStudySession } from '../../workspace/study/studyConfig'
import { aiChatEnabled, isPythonFamily } from '../sessionSettings'

// Which student UI a session renders, and where its student link points.
//
// /workspace/:id is the student page for every session it can run: the web (GUI) exercises
// and the Python family (Python, and 'Vizmental' = Python with AI chat). Everything else -
// C++, SQL, Blockly, Audio, Pop Quiz and the legacy group types - is attempted on
// /session/:id (Home.jsx), because /workspace cannot run it. Home.jsx, WorkspacePage.jsx and
// every link builder (Distribute, the lobby link, the session list) use opensInWorkspace, and
// the wizard preview and the Session Setting tab use studentSurfaceFor, so what instructors
// preview cannot drift from what students get.
//
// Takes a session-like object ({ type, id? }) rather than a bare type: isGuiSession also
// matches a hardcoded session id, and the study sessions below run on /workspace by id.

// CHI / class-deployment / user-study sessions, which /workspace runs by hardcoded id
// (WorkspacePage.jsx, Workspace.jsx, languageConfig.js, QuizContext.js), whatever their type.
const WORKSPACE_STUDY_SESSION_IDS = [
  643, 654, 655, 658, 659, 660, 661, 662, 663, 664, 665, 666, 668, 669, 674, 675, 676, 677, 678,
]

export const isWorkspaceStudySession = (session) =>
  Boolean(session) && WORKSPACE_STUDY_SESSION_IDS.includes(Number(session.id))

export const opensInWorkspace = (session) => {
  if (!session) return false
  if (isWorkspaceStudySession(session)) return true
  if (!session.type) return false
  return isGuiSession(session) || isPythonFamily(session.type)
}

// Whether students on /workspace/:id get the AI assistant column. Python-family sessions use
// aiChatEnabled - the rule behind the instructor's ENABLE AI CHAT switch, under which a plain
// 'Python' row has no chat. Web exercises and the study sessions keep the rule they always
// had on /workspace (enable_chat), so running and past studies behave exactly as before.
export const workspaceShowsAiChat = (session) => {
  if (!session || !isAIEnabled(session.id)) return false
  if (isGuiSession(session) || isWorkspaceStudySession(session)) return Boolean(session.enable_chat)
  return aiChatEnabled(session)
}

// Whether students on /workspace/:id see the end-of-session review survey: End Session on a
// Python-family session turns chat off with closeSessionType 'closed with survey', and
// VizmentalIDE showed the survey in its AI column then. Study sessions keep their own flow:
// the classroom study's conditions (studyConfig.js) end with their concept probes instead.
export const workspaceShowsEndSurvey = (session) =>
  Boolean(session) &&
  isPythonFamily(session.type) &&
  !isWorkspaceStudySession(session) &&
  !isStudySession(session) &&
  !session.enable_chat &&
  session.closeSessionType === 'closed with survey'

// Where a student is sent for this session: /workspace/:id or /session/:id.
export const studentPathFor = (session) =>
  `/${opensInWorkspace(session) ? 'workspace' : 'session'}/${session?.id}`

// The classic Python IDE students got on /session/:id before /workspace became their page.
// Python-family students are now redirected to /workspace; VizmentalIDE stays in the code.
export const usesVizmentalIDE = (session) =>
  session?.type === 'Vizmental' || session?.type === 'Python'

// 'workspace' | 'quiz' | 'audio' | 'studentPanel' | null
export const studentSurfaceFor = (session) => {
  if (!session?.type) return null
  if (isGuiSession(session) || isPythonFamily(session.type)) return 'workspace'
  if (session.type === 'Pop Quiz') return 'quiz'
  if (session.type === 'Audio') return 'audio'
  return 'studentPanel'
}
